// ===========================================
// Drive File Picker Component
// ===========================================
// Allows admin to browse Google Drive folders and select a file
// Used in Piece Library to select sheet music scores

import { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Breadcrumbs,
  Link,
  Typography,
  Skeleton,
  Alert,
  InputAdornment,
  Paper,
  Chip,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ArrowBack as BackIcon,
  Search as SearchIcon,
  ChevronRight as ChevronRightIcon,
  Home as HomeIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  AudioFile as AudioIcon,
  Description as DocIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useBrowseFolders, useBrowseFiles, useGoogleDriveAuthStatus } from '../../hooks/useGoogleDrive';
import { DriveFolder, DriveBrowseFile } from '../../api/googleDrive.api';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

// ===========================================
// TYPES
// ===========================================

export interface SelectedDriveFile {
  id: string;
  name: string;
  webViewLink: string;
  mimeType: string;
}

interface DriveFilePickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (file: SelectedDriveFile) => void;
  title?: string;
  fileTypes?: string[];
}

interface BreadcrumbItem {
  id: string;
  name: string;
}

// ===========================================
// HELPERS
// ===========================================

function getFileIcon(mimeType: string) {
  if (mimeType === 'application/pdf') return <PdfIcon color="error" />;
  if (mimeType.startsWith('image/')) return <ImageIcon color="primary" />;
  if (mimeType.startsWith('audio/')) return <AudioIcon color="secondary" />;
  if (mimeType.includes('document') || mimeType.includes('word') || mimeType === 'text/plain')
    return <DocIcon color="info" />;
  return <FileIcon color="action" />;
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ===========================================
// COMPONENT
// ===========================================

export default function DriveFilePicker({
  open,
  onClose,
  onSelect,
  title = 'Select File from Google Drive',
  fileTypes,
}: DriveFilePickerProps) {
  const [currentParentId, setCurrentParentId] = useState<string | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [selectedFile, setSelectedFile] = useState<DriveBrowseFile | null>(null);

  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // Check if Drive is connected
  const { data: authStatus } = useGoogleDriveAuthStatus();
  const isConnected = authStatus?.isConnected ?? false;

  // Query folders
  const { data: folders, isLoading: foldersLoading } = useBrowseFolders(
    {
      parentId: currentParentId,
      query: debouncedQuery || undefined,
    },
    { enabled: open && isConnected }
  );

  // Query files in current folder
  const { data: files, isLoading: filesLoading } = useBrowseFiles(
    { parentId: currentParentId || '' },
    { enabled: open && isConnected && !!currentParentId }
  );

  const isLoading = foldersLoading || filesLoading;

  // Filter files by type if specified
  const filteredFiles = useMemo(() => {
    if (!files) return [];
    if (!fileTypes || fileTypes.length === 0) return files;
    return files.filter((f) =>
      fileTypes.some((type) => f.mimeType.includes(type))
    );
  }, [files, fileTypes]);

  const folderList = useMemo(() => folders || [], [folders]);

  // Navigate into a folder
  const handleNavigateInto = useCallback((folder: DriveFolder) => {
    setCurrentParentId(folder.id);
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
    setSearchQuery('');
    setSelectedFile(null);
  }, []);

  // Navigate to breadcrumb
  const handleBreadcrumbClick = useCallback((index: number) => {
    if (index === -1) {
      setCurrentParentId(undefined);
      setBreadcrumbs([]);
    } else {
      const targetFolder = breadcrumbs[index];
      setCurrentParentId(targetFolder.id);
      setBreadcrumbs((prev) => prev.slice(0, index + 1));
    }
    setSearchQuery('');
    setSelectedFile(null);
  }, [breadcrumbs]);

  // Go back one level
  const handleGoBack = useCallback(() => {
    if (breadcrumbs.length === 0) return;
    if (breadcrumbs.length === 1) {
      setCurrentParentId(undefined);
      setBreadcrumbs([]);
    } else {
      const newBreadcrumbs = breadcrumbs.slice(0, -1);
      setCurrentParentId(newBreadcrumbs[newBreadcrumbs.length - 1].id);
      setBreadcrumbs(newBreadcrumbs);
    }
    setSearchQuery('');
    setSelectedFile(null);
  }, [breadcrumbs]);

  const handleSelectFile = useCallback((file: DriveBrowseFile) => {
    setSelectedFile(file);
  }, []);

  const handleConfirm = useCallback(() => {
    if (selectedFile) {
      onSelect({
        id: selectedFile.id,
        name: selectedFile.name,
        webViewLink: selectedFile.webViewLink,
        mimeType: selectedFile.mimeType,
      });
      // Reset state on close
      setCurrentParentId(undefined);
      setBreadcrumbs([]);
      setSearchQuery('');
      setSelectedFile(null);
      onClose();
    }
  }, [selectedFile, onSelect, onClose]);

  const handleClose = useCallback(() => {
    setCurrentParentId(undefined);
    setBreadcrumbs([]);
    setSearchQuery('');
    setSelectedFile(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {!isConnected ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Google Drive is not connected. Please connect Google Drive in Admin &gt; Google Drive settings first.
          </Alert>
        ) : (
          <Box sx={{ mt: 1 }}>
            {/* Search */}
            <TextField
              fullWidth
              size="small"
              placeholder="Search folders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Breadcrumb Navigation */}
            <Paper variant="outlined" sx={{ p: 1, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {breadcrumbs.length > 0 && (
                  <IconButton size="small" onClick={handleGoBack}>
                    <BackIcon fontSize="small" />
                  </IconButton>
                )}
                <Breadcrumbs
                  separator={<ChevronRightIcon fontSize="small" />}
                  sx={{ flex: 1, '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' } }}
                >
                  <Link
                    component="button"
                    variant="body2"
                    underline="hover"
                    color={breadcrumbs.length === 0 ? 'text.primary' : 'inherit'}
                    onClick={() => handleBreadcrumbClick(-1)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <HomeIcon fontSize="small" />
                    My Drive
                  </Link>
                  {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return isLast ? (
                      <Typography key={crumb.id} variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                        {crumb.name}
                      </Typography>
                    ) : (
                      <Link
                        key={crumb.id}
                        component="button"
                        variant="body2"
                        underline="hover"
                        onClick={() => handleBreadcrumbClick(index)}
                      >
                        {crumb.name}
                      </Link>
                    );
                  })}
                </Breadcrumbs>
              </Box>
            </Paper>

            {/* Loading */}
            {isLoading && (
              <Box>
                <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={48} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={48} />
              </Box>
            )}

            {/* Content List */}
            {!isLoading && (
              <>
                {folderList.length === 0 && filteredFiles.length === 0 ? (
                  <Box
                    sx={{
                      py: 4,
                      textAlign: 'center',
                      color: 'text.secondary',
                      border: '1px dashed',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <FolderOpenIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                    <Typography variant="body2">
                      {currentParentId
                        ? 'No files or folders found in this location'
                        : 'Navigate into a folder to see files'}
                    </Typography>
                  </Box>
                ) : (
                  <List
                    dense
                    sx={{
                      maxHeight: 350,
                      overflow: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                    }}
                  >
                    {/* Folders first */}
                    {folderList.map((folder) => (
                      <ListItem key={`folder-${folder.id}`} disablePadding>
                        <ListItemButton
                          onClick={() => handleNavigateInto(folder)}
                          sx={{ pr: 6 }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            <FolderIcon color="action" />
                          </ListItemIcon>
                          <ListItemText
                            primary={folder.name}
                            primaryTypographyProps={{ noWrap: true }}
                          />
                          <ChevronRightIcon color="action" sx={{ ml: 1 }} />
                        </ListItemButton>
                      </ListItem>
                    ))}

                    {/* Files */}
                    {filteredFiles.map((file) => {
                      const isSelected = selectedFile?.id === file.id;
                      return (
                        <ListItem key={`file-${file.id}`} disablePadding>
                          <ListItemButton
                            selected={isSelected}
                            onClick={() => handleSelectFile(file)}
                          >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              {getFileIcon(file.mimeType)}
                            </ListItemIcon>
                            <ListItemText
                              primary={file.name}
                              secondary={formatFileSize(file.size)}
                              primaryTypographyProps={{
                                noWrap: true,
                                fontWeight: isSelected ? 600 : 400,
                              }}
                            />
                            {isSelected && (
                              <Chip
                                label="Selected"
                                size="small"
                                color="primary"
                                icon={<CheckIcon />}
                              />
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </>
            )}

            {/* Help text */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Navigate into folders to find files. Click a file to select it.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selectedFile}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
}
